import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import moment from "moment"

import Layout from "../../components/Layout"
import CartDealCard from "../../components/cart/CartDealCard"
import CartProductCard from "../../components/cart/CartProductCard"

import * as cartSlice from "../../store/cart"
import * as customerApis from "../../utilities/apis/customer"
import * as zoneApis from "../../utilities/apis/zone"
import * as orderApis from "../../utilities/apis/order"
import { isDeal, isProduct } from "../../utilities/enumerations/constants"
import * as functions from "../../utilities/enumerations/functions"

const Checkout = () => {
  const { token } = useSelector(state => state.user)
  const cartState = useSelector(state => state.cart)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const mobileRef = useRef(null)
  const zoneRef = useRef(null)
  const addressRef = useRef(null)
  const dateRef = useRef(null)
  const timeRef = useRef(null)
  const noteRef = useRef(null)
  const discountRef = useRef(null)
  const totalRef = useRef(null)
  const [discount, setDiscount] = useState(0)
  const [customers, setCustomers] = useState([])
  const [zones, setZones] = useState([])
  const [active, setActive] = useState(-1)
  const [activeZone, setActiveZone] = useState(-1)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    try {
      setLoading(true)
      e.preventDefault()

      const shipDatetime = moment(`${dateRef.current.value} ${timeRef.current.value}`)
      const products = cartState.pos.filter((lineitem) => isProduct(lineitem.type)).map((lineitem) => ({
        cost: lineitem.cost,
        price: lineitem.price,
        quantity: cartState.posById[lineitem.slug],
        product_id: lineitem.id
      }))
      const orderDeals = cartState.pos.filter((lineitem) => isDeal(lineitem.type))
      const deals = []

      orderDeals.forEach((lineitem) => lineitem.deal_products.map((dealProduct) => {
        deals.push({
          cost: dealProduct.product.cost,
          price: dealProduct.product.price,
          quantity: dealProduct.quantity,
          deal_cost: lineitem.cost,
          deal_price: lineitem.price,
          deal_discount: lineitem.discount,
          deal_quantity: cartState.posById[lineitem.slug],
          swap: dealProduct.swap || 0,
          product_id: dealProduct.product_id,
          deal_id: lineitem.id
        })
      }))
      const lineitems = products.concat(deals)

      const payload = {
        zone: {
          name: zoneRef.current.value
        },
        customer: {
          mobile: mobileRef.current.value,
          address: addressRef.current.value
        },
        order: {
          discount: parseFloat(discountRef.current.value),
          customer_note: noteRef.current.value,
          ship_datetime: shipDatetime.format("YYYY-MM-DD HH:mm"),
          total: parseFloat(totalRef.current.value) + discount,
          cost: cartState.pos.reduce((previous, current) => previous += (cartState.posById[current.slug] * current.cost), 0),
          commission: 0
        },
        order_lineitems: lineitems
      }

      const response = await orderApis.createOrder(token, payload)
      if (!response.status) throw new Error(response.message)

      dispatch(cartSlice.emptyPos())
      alert("Order punched")
      navigate("/dashboard")
    } catch (error) {
      alert(error.message)
    } finally {
      setLoading(false)
    }
  }

  const goCDown = () => {
    setActive((previous) => {
      if (previous >= customers.length)
        previous = 0
      else
        previous += 1

      mobileRef.current.value = customers[previous]?.mobile || ""
      return previous
    })
  }

  const goCUp = () => {
    setActive((previous) => {
      if (previous <= 0)
        previous = customers.length
      else
        previous -= 1

      mobileRef.current.value = customers[previous]?.mobile || ""
      return previous
    })
  }

  const handleMobile = async (e) => {
    try {
      const mobile = e.target.value
      if (mobile.length < 3) throw new Error("a")

      const response = await customerApis.getCustomers(token, functions.paramsToQuery({ mobile }))
      if (!response.status) throw new Error(response.message)
      if (response.customers && response.customers.length === 0) throw new Error(response.message)

      setCustomers(response.customers)
    } catch (error) {
      addressRef.current.value = ""
      setCustomers([])
    }
  }

  const handleMobileKeyup = (e) => {
    if (e.which === 40) goCDown()
    if (e.which === 38) goCUp()
  }

  const handleMobileBlur = (e) => {
    const customer = customers.find((customer) => customer.mobile === e.target.value)
    if (!customer) {
      setTimeout(() => {
        setActive(-1)
        setCustomers([])
      }, 100)
      return
    }

    mobileRef.current.value = customer.mobile
    addressRef.current.value = customer.address

    setTimeout(() => {
      setActive(-1)
      setCustomers([])
    }, 100)
  }

  const handleMobileSelect = (value) => {
    const customer = customers.find((customer) => customer.mobile === value)
    if (!customer) return

    mobileRef.current.value = customer.mobile
    addressRef.current.value = customer.address
    setCustomers([])
  }

  const goZDown = () => {
    setActiveZone((previous) => {
      if (previous >= zones.length)
        previous = 0
      else
        previous += 1

      zoneRef.current.value = zones[previous]?.name || ""
      return previous
    })
  }

  const goZUp = () => {
    setActiveZone((previous) => {
      if (previous <= 0)
        previous = zones.length
      else
        previous -= 1

      zoneRef.current.value = zones[previous]?.name || ""
      return previous
    })
  }

  const handleZone = async (e) => {
    try {
      const name = e.target.value
      if (name.length < 1) throw new Error("a")

      const response = await zoneApis.getZones(token, functions.paramsToQuery({ name }))
      if (!response.status) throw new Error(response.message)
      if (response.zones && response.zones.length === 0) throw new Error(response.message)

      setZones(response.zones)
    } catch (error) {
      setZones([])
    }
  }

  const handleZoneKeyup = (e) => {
    if (e.which === 40) goZDown()
    if (e.which === 38) goZUp()
  }

  const handleZoneBlur = (e) => {
    const zone = zones.find((zone) => zone.name === e.target.value)
    if (!zone) {
      setTimeout(() => {
        setActiveZone(-1)
        setZones([])
      }, 100)
      return
    }

    zoneRef.current.value = zone.name

    setTimeout(() => {
      setActiveZone(-1)
      setZones([])
    }, 100)
  }

  const handleZoneSelect = (value) => {
    const zone = zones.find((zone) => zone.name === value)
    if (!zone) return

    zoneRef.current.value = zone.name
    setZones([])
  }

  const handleDiscount = (e) => {
    setDiscount(parseFloat(e.target.value || "0"))
  }

  useEffect(() => {
    const customer = JSON.parse(sessionStorage.getItem("customer"))
    if (customer) {
      mobileRef.current.value = customer.mobile
      addressRef.current.value = customer.address
      zoneRef.current.value = customer.zone
    }

    dateRef.current.defaultValue = moment().format("YYYY-MM-DD")
    timeRef.current.defaultValue = moment().format("HH:mm")
  }, [])

  const cartSwap = cartState.pos.filter((item) => isDeal(item.type)).reduce((p, c) => p += cartState.posById[c.slug] * c.deal_products.reduce((dp, dc) => dp += dc.swap || 0, 0), 0)
  const cartTotal = cartState.pos.reduce((p, c) => p += (cartState.posById[c.slug] * (c.price - (c.discount || 0))), 0) + cartSwap

  return (
    <Layout
      header={true}
      footer={true}
    >
      <div className="container-fluid">
        <main id="order">
          <form method="POST" onSubmit={handleSubmit}>
            <section className="p-2">
              <div className="card rounded-4 shadow-none mb-3">
                <div className="card-body py-4">
                  <p className="lead text-body-emphasis fw-bold">Customer</p>
                  <div className="row">
                    <div className="col-md-6">
                      <div className={`form-autocomplete mb-3 ${customers.length > 0 ? "show" : "hide"}`}>
                        <label htmlFor="mobile" className="form-label">Mobile #</label>
                        <input ref={mobileRef} type="text" className="form-control rounded-3" id="mobile" onChange={handleMobile} onKeyUp={handleMobileKeyup} onBlur={handleMobileBlur} required />

                        <ul className={`autocomplete ${customers.length > 0 ? "show" : "hide"}`}>
                          {customers.map((customer, index) => (
                            <li key={`customer-${index.toString()}`} className={active === index ? "active" : ""} onClick={() => handleMobileSelect(customer.mobile)}>{customer.mobile}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className={`form-autocomplete mb-3 ${zones.length > 0 ? "show" : "hide"}`}>
                        <label htmlFor="zone" className="form-label">Zone</label>
                        <input ref={zoneRef} type="text" className="form-control rounded-3" id="zone" onChange={handleZone} onKeyUp={handleZoneKeyup} onBlur={handleZoneBlur} required />

                        <ul className={`autocomplete ${zones.length > 0 ? "show" : "hide"}`}>
                          {zones.map((zone, index) => (
                            <li key={`zone-${index.toString()}`} className={activeZone === index ? "active" : ""} onClick={() => handleZoneSelect(zone.name)}>{zone.name}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="address" className="form-label">Address</label>
                      <textarea ref={addressRef} className="form-control rounded-3" id="address" required rows={4}></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card rounded-4 shadow-none mb-3">
                <div className="card-body py-4">
                  <p className="lead text-body-emphasis fw-bold">Order</p>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="date" className="form-label">Ship Date</label>
                      <input ref={dateRef} type="date" className="form-control rounded-3" id="date" required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="time" className="form-label">Ship Time</label>
                      <input ref={timeRef} type="time" className="form-control rounded-3" id="time" required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="note" className="form-label">Note</label>
                      <textarea ref={noteRef} className="form-control rounded-3" id="note" rows={4}></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-end">
                <button type="submit" className="btn btn-theme rounded-4 w-25" disabled={loading}>Confirm</button>
              </div>
            </section>
            <aside className="d-flex flex-column p-2">
              <div className="border border-bottom-0 rounded-top-4 shadow-none">
                <p className="lead text-body-emphasis fw-bold m-0 p-2">Items</p>
                <ul className="flex-grow-1 overflow-y-auto list-group list-group-flush shadow-none">
                  {cartState.pos.map((item, index) => {
                    return (
                      item.type === "deal"
                        ? <CartDealCard key={`cart-item-${index}`} item={item} index={index} />
                        : <CartProductCard key={`cart-item-${index}`} item={item} />
                    )
                  })}
                </ul>
              </div>
              <div className="cart-footer bg-light mb-3 border rounded-bottom-4 p-2">
                <div className="row justify-content-between align-items-center g-0 mb-1">
                  <div className="col-md-8">
                    <p className="lead text-body-tertiary fw-semibold m-0">Discount</p>
                  </div>
                  <div className="col-md-4">
                    <input ref={discountRef} type="number" className="form-control form-control-sm rounded-3 text-end" id="discount" onKeyUp={handleDiscount} onChange={handleDiscount} defaultValue={discount.toFixed(2)} required />
                  </div>
                </div>
                <div className="row justify-content-between align-items-center g-0 mb-1">
                  <div className="col-md-8">
                    <p className="lead text-body-tertiary fw-semibold m-0">Swap</p>
                  </div>
                  <div className="col-md-4">
                    <input type="number" className="form-control form-control-sm rounded-3 text-end" id="swap" disabled required value={cartSwap.toFixed(2)} />
                  </div>
                </div>
                <div className="row justify-content-between align-items-center g-0">
                  <div className="col-md-8">
                    <p className="lead text-body-tertiary fw-semibold m-0">Total</p>
                  </div>
                  <div className="col-md-4">
                    <input ref={totalRef} type="number" className="form-control form-control-sm rounded-3 text-end" id="total" disabled value={(cartTotal - discount).toFixed(2)} />
                  </div>
                </div>
              </div>
            </aside>
          </form>
        </main>
      </div>
    </Layout>
  )
}

export default Checkout
